import React from 'react';
import { getQcmCanonicalMetadata } from '../../utils/qcmMetadata';

/** Libellé court pour la ligne repliée */
export function getRevisionStrategyShortLabel(filterType, isReinforcementWeaknessActive) {
    if (isReinforcementWeaknessActive && filterType === 'weakness_all') {
        return 'Global weakness reinforcement';
    }
    switch (filterType) {
        case 'weakness_all':
            return 'Global weakness reinforcement';
        case 'weakness_granular':
        case 'weakness_granular_tags':
            return 'Specific weakness reinforcement';
        case 'manual_domain':
            return 'by domain';
        case 'manual_task':
            return 'by sub-domain';
        case 'left_topics':
            return 'under-practiced topics';
        case 'selected_qcm':
            return 'by selected QCM';
        case 'selected_flashcard':
            return 'by selected Flashcard';
        case 'keywords_fuzzy':
            return 'by keywords';
        case 'unseen_qcm':
            return 'Unseen QCM';
        case 'free_tour':
        case 'public_free_random':
            return 'Free tour';
        default:
            return 'Random';
    }
}

/** Description explicite (dépliée) */
export function getRevisionStrategyDescription(filterType, isReinforcementWeaknessActive) {
    if (isReinforcementWeaknessActive && filterType === 'weakness_all') {
        return 'Questions aligned with your make-up session queue.';
    }
    switch (filterType) {
        case 'weakness_all':
            return 'Questions prioritized on reinforcement sessions across topics.';
        case 'weakness_granular':
        case 'weakness_granular_tags':
            return 'Questions filtered to concepts selected from your weakness matrix (keyword tags).';
        case 'manual_domain':
            return 'Questions filtered to the domain(s) you selected.';
        case 'manual_task':
            return 'Questions filtered to the sub-domain(s) you selected.';
        case 'left_topics':
            return 'Questions filtered to under-practiced sub-domains (< 3 answered questions).';
        case 'selected_qcm':
            return 'Questions drawn from the QCM you pinned in your selection list.';
        case 'selected_flashcard':
            return 'Flashcards drawn from the items you pinned in your selection list.';
        case 'keywords_fuzzy':
            return 'Questions ranked by fuzzy match against the keywords you provided.';
        case 'unseen_qcm':
            return 'Only questions you have not answered yet in this exam.';
        case 'free_tour':
        case 'public_free_random':
            return 'Trial mode: random pick among the publicly available (extract_free) questions.';
        default:
            return 'Questions drawn from the exam pool in random order (default).';
    }
}

function normalizeTask(s) {
    return String(s || '').trim().replace(/\.$/, '');
}

export function findCatalogWeightPercent(catalog, domain, task) {
    const rows = catalog?.subdomains || catalog?.tasks || [];
    const d = String(domain || '').trim();
    const t = normalizeTask(task);
    const row = rows.find((r) => {
        const pd = String(r.parent_domain || '').trim();
        const sd = normalizeTask(r.sub_domain || r.task);
        return pd === d && sd === t;
    });
    return row?.weight_percent;
}

/**
 * Bandeau discret : stratégie de révision + métadonnées (domaine, task, %).
 * variant 'results' : affiche les mots-clés de recherche (granular) en bas si fournis.
 */
const RevisionStrategyInfoBar = ({
    revisionStrategy,
    isReinforcementWeaknessActive,
    isTargetWeaknessActive,
    currentQCM,
    examCatalog,
    onOpenStrategyModal,
    expanded,
    onToggleExpanded,
    variant = 'question',
    resultsKeywords = null,
}) => {
    const md = currentQCM?.metadata || {};
    const canonical = getQcmCanonicalMetadata(currentQCM);
    const domain =
        canonical.domain ||
        md.Domain ||
        md.domain ||
        currentQCM?.domain ||
        currentQCM?.theme ||
        '';
    const task =
        canonical.task ||
        md.Task ||
        md.task ||
        md.Sub_domain ||
        md.sub_domain ||
        currentQCM?.task ||
        currentQCM?.sub_domain ||
        '';
    const topicPct = md['Topic_Occurrence%'] ?? md.topic_occurrence_pct;

    const shortLabel = getRevisionStrategyShortLabel(
        revisionStrategy.filterType,
        isReinforcementWeaknessActive
    );
    const longDesc = getRevisionStrategyDescription(
        revisionStrategy.filterType,
        isReinforcementWeaknessActive
    );
    const weight = findCatalogWeightPercent(examCatalog, domain, task);

    const selectionDomain =
        revisionStrategy.filterType === 'manual_domain' && revisionStrategy.domainSelection?.length
            ? revisionStrategy.domainSelection.join(', ')
            : null;
    const selectionTask =
        revisionStrategy.filterType === 'manual_task' && revisionStrategy.taskSelection?.length
            ? revisionStrategy.taskSelection.join(', ')
            : null;
    const keywordChips =
        revisionStrategy.filterType === 'keywords_fuzzy' &&
        Array.isArray(revisionStrategy.keywordSelection)
            ? revisionStrategy.keywordSelection
                .map((k) => String(k || '').trim())
                .filter(Boolean)
            : [];

    const displayTaskLine = task || selectionTask || '';
    const isCollapsed = !expanded;
    const sameTaskAsSelection =
        selectionTask &&
        task &&
        String(selectionTask).trim().toLowerCase() === String(task).trim().toLowerCase();

    return (
        <div className="mb-4 rounded-lg border border-orange-900/40 bg-gray-900/50 px-2.5 py-2">
            {/* Ligne supérieure : icône + libellé "Strategy" + valeur (avec keywords éventuels) + crayon
                — tous alignés au centre vertical. La colonne centrale wrappe sur plusieurs lignes
                si la stratégie est longue, sans pousser le crayon hors de la barre. */}
            <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-1.5 shrink-0 self-start">
                    <div className="rounded-md p-1.5 text-orange-400/90" aria-hidden>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 13a3 3 0 013 3v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4a3 3 0 013-3z"
                            />
                        </svg>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Strategy
                    </span>
                </div>
                <div
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1 cursor-pointer"
                    onClick={onToggleExpanded}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onToggleExpanded();
                        }
                    }}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse details' : 'Expand details'}
                >
                    <span className="min-w-0 whitespace-normal break-words text-[13px] leading-snug font-medium text-orange-200/95">
                        {shortLabel}
                    </span>
                    {keywordChips.length > 0 && (
                        <span className="inline-flex flex-wrap items-center gap-1">
                            <span className="text-[11px] text-gray-500" aria-hidden>:</span>
                            {keywordChips.map((kw, i) => (
                                <span
                                    key={`kw-${i}-${kw}`}
                                    className="max-w-full break-words rounded bg-orange-950/40 px-1.5 py-0.5 text-[11px] font-medium text-orange-100/95"
                                    title={kw}
                                >
                                    {kw}
                                </span>
                            ))}
                        </span>
                    )}
                    {revisionStrategy.studyMode === 'flashcard' && (
                        <span className="rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-200/90">
                            Flashcard
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onOpenStrategyModal}
                    className="shrink-0 self-start rounded-md p-1.5 text-gray-400 hover:bg-gray-800/80 hover:text-teal-300/90"
                    title="Change revision strategy"
                    aria-label="Change revision strategy"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                    </svg>
                </button>
            </div>
            <div className="min-w-0 pl-0 sm:pl-[calc(1.25rem+1.25rem+0.5rem)] pt-0.5">
                    {!isCollapsed && (
                        <p className="text-xs leading-snug text-gray-500">{longDesc}</p>
                    )}
                    {!isCollapsed && (domain || displayTaskLine) && (
                        <div className="mt-1.5 text-xs text-gray-400 line-clamp-2" title={`${domain || ''}${displayTaskLine ? ` · ${displayTaskLine}` : ''}`}>
                            <span className="text-gray-500">This question: </span>
                            <span className="text-gray-300">{domain || '—'}</span>
                            {displayTaskLine && !sameTaskAsSelection ? (
                                <span className="text-gray-300"> · {displayTaskLine}</span>
                            ) : null}
                        </div>
                    )}
                    {!isCollapsed && variant === 'results' &&
                        resultsKeywords &&
                        Array.isArray(resultsKeywords) &&
                        resultsKeywords.length > 0 && (
                            <div className="mt-2 border-t border-gray-700/40 pt-2">
                                <div className="text-[10px] uppercase tracking-wide text-gray-600">
                                    Search keywords used (specific weakness)
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {resultsKeywords.map((kw, i) => (
                                        <span
                                            key={`${kw}-${i}`}
                                            className="rounded bg-gray-800/80 px-2 py-0.5 text-[11px] text-gray-300"
                                        >
                                            {kw}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    {expanded && (
                        <div className="mt-2 space-y-1.5 text-xs leading-relaxed text-gray-400">
                            {isTargetWeaknessActive && (
                                <div className="text-gray-500">
                                    Target weakness (scoring) is active for answer evaluation.
                                </div>
                            )}
                            {selectionDomain && (
                                <div>
                                    <span className="text-gray-600">Domain(s) you selected: </span>
                                    <span className="text-gray-300">{selectionDomain}</span>
                                </div>
                            )}
                            {selectionTask && (
                                <div>
                                    <span className="text-gray-600">Sub-domain(s) you selected: </span>
                                    <span className="text-gray-300">{selectionTask}</span>
                                </div>
                            )}
                            {revisionStrategy.filterType === 'manual_domain' && selectionDomain && (
                                <div>
                                    <span className="text-gray-600">Domain used to filter: </span>
                                    <span className="text-gray-300">{selectionDomain}</span>
                                </div>
                            )}
                            <div className="border-t border-gray-700/60 pt-2">
                                <div className="text-[11px] uppercase tracking-wide text-gray-600">This question</div>
                                <div className="mt-0.5 text-[11px] text-gray-500">{domain || '—'}</div>
                                {!(sameTaskAsSelection && task) && (
                                    <div className="text-sm text-gray-300">{task || '—'}</div>
                                )}
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                                    {weight != null && (
                                        <span>
                                            Weight in exam: <span className="text-gray-400">{weight}%</span>
                                        </span>
                                    )}
                                    {topicPct != null && topicPct !== '' && (
                                        <span>
                                            Topic occurrence: <span className="text-gray-400">{topicPct}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default RevisionStrategyInfoBar;
